import React, { createContext, useContext, useState, ReactNode } from 'react';
import { CartItem, Meal } from '@/types/meal';

interface CartContextType {
  items: CartItem[];
  addItem: (meal: Meal, removedIngredients?: string[], selectedAddons?: string[], notes?: string) => void;
  removeItem: (itemId: string) => void;
  updateQuantity: (itemId: string, quantity: number) => void;
  clearCart: () => void;
  totalItems: number;
  totalPrice: number;
}

const CartContext = createContext<CartContextType | undefined>(undefined);

export function CartProvider({ children }: { children: ReactNode }) {
  const [items, setItems] = useState<CartItem[]>([]);

  const addItem = (
    meal: Meal,
    removedIngredients: string[] = [],
    selectedAddons: string[] = [],
    notes?: string
  ) => {
    const newItem: CartItem = {
      id: `${meal.id}-${Date.now()}`,
      meal,
      quantity: 1,
      removedIngredients,
      selectedAddons,
      notes,
    };
    setItems((prev) => [...prev, newItem]);
  };

  const removeItem = (itemId: string) => {
    setItems((prev) => prev.filter((item) => item.id !== itemId));
  };

  const updateQuantity = (itemId: string, quantity: number) => {
    if (quantity < 1) {
      removeItem(itemId);
      return;
    }
    setItems((prev) =>
      prev.map((item) => (item.id === itemId ? { ...item, quantity } : item))
    );
  };

  const clearCart = () => {
    setItems([]);
  };

  const totalItems = items.reduce((sum, item) => sum + item.quantity, 0);

  const totalPrice = items.reduce((sum, item) => {
    const addonPrices = item.selectedAddons.reduce((addonSum, addonId) => {
      const addon = item.meal.addons?.find((a) => a.id === addonId);
      return addonSum + (addon?.priceInCents || 0);
    }, 0);
    return sum + (item.meal.priceInCents + addonPrices) * item.quantity;
  }, 0);

  return (
    <CartContext.Provider
      value={{
        items,
        addItem,
        removeItem,
        updateQuantity,
        clearCart,
        totalItems,
        totalPrice,
      }}
    >
      {children}
    </CartContext.Provider>
  );
}

export function useCart() {
  const context = useContext(CartContext);
  if (!context) {
    throw new Error('useCart must be used within a CartProvider');
  }
  return context;
}
